import { Theme } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import Highcharts, { pad } from 'highcharts';
import HighchartsReact, {
  HighchartsReactRefObject,
} from 'highcharts-react-official';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import { forwardRef, Ref } from 'react';
import { toAbsoluteUrl } from '../../../_metronic/_helpers';
HighchartsAccessibility(Highcharts);

const getLogo = () => {
  return toAbsoluteUrl('/media/logos/Humaaans - 1 Character (1).svg');
};

const useStyles = makeStyles((theme: Theme) => ({
  highcharts:
    theme.palette.mode === 'dark'
      ? {
          '& .highcharts-background': {
            fill: '#121c21',
          },
          '& .highcharts-axis-labels text': {
            fill: '#ffffff !important',
          },
          '& .highcharts-title tspan': {
            color: '#ffffff',
            fill: '#ffffff',
          },
          '& .highcharts-yaxis text tspan': {
            color: '#ffffff',
            fill: '#ffffff',
          },
          '& .highcharts-legend-item text tspan': {
            color: '#ffffff',
            fill: '#ffffff',
          },
          '& .highcharts-legend-item text': {
            fill: '#ffffff !important',
          },
        }
      : {},
  noDataContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  noDataBackground: {
    backgroundColor: theme.palette.mode === 'dark' ? '#111111' : '#eeeeee',
  },
  noDataMessage: {
    position: 'absolute',
    left: '50%',
    top: '50%',
  },
  noDataIconContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  noDataIcon: {
    margin: 'auto',
  },
  barSkeleton: {
    margin: 10,
    height: 20,
    backgroundColor: theme.palette.mode === 'dark' ? 'dimgray' : 'gainsboro',
  },
  barSkeleton90: {
    width: '90%',
  },
  barSkeleton80: {
    width: '80%',
  },
  barSkeleton70: {
    width: '70%',
  },
  barSkeleton60: {
    width: '60%',
  },
  barSkeleton50: {
    width: '50%',
  },
  barSkeleton40: {
    width: '40%',
  },
  barSkeleton30: {
    width: '30%',
  },
  barSkeleton20: {
    width: '20%',
  },
  barSkeleton10: {
    width: '10%',
  },
}));

interface IHighchartsReactWithStyles {
  /** Set to true to show loading skeleton */
  loading?: boolean;
  options: Highcharts.Options;
}

export const HighchartsReactWithStyles = forwardRef(
  (
    props: IHighchartsReactWithStyles & HighchartsReact.Props,
    ref: Ref<HighchartsReactRefObject> | undefined
  ) => {
    const classes = useStyles();
    // Define the tooltip formatter function
    const tooltipFormatter = function () {
      // @ts-ignore
      const titleText = props.options?.yAxis?.title?.text;
      if (titleText === 'Total Duration' || titleText === 'Avg. Duration') {
        const formatted = hhmmss(
          // @ts-ignore
          this.y
        );
        // @ts-ignore
        return `${this.series.name}<br/>${this.x}: <b>${formatted}</b><br/>`;
      }
      // @ts-ignore
      return `<b>${this.series.name}</b><br/>${this.x}: ${this.y}<br/>`;
    };

    // Add the tooltip configuration to the options
    const tlTip = props?.options?.tooltip
      ? props?.options?.tooltip
      : { formatter: tooltipFormatter };

    const optionsWithTooltip = {
      ...props.options,
      tooltip: tlTip,
    };

    return (!props.loading && (optionsWithTooltip?.series?.[0] as any))?.data
      ?.length ? (
      <div className={classes.highcharts}>
        {ref ? (
          <HighchartsReact ref={ref} {...props} options={optionsWithTooltip} />
        ) : (
          <HighchartsReact {...props} options={optionsWithTooltip} />
        )}
      </div>
    ) : props.loading ? (
      <div className={classes.noDataContainer}>
        <Skeleton
          className={clsx(classes.barSkeleton, classes.barSkeleton80)}
          variant="rectangular"
        />
        <Skeleton
          className={clsx(classes.barSkeleton, classes.barSkeleton70)}
          variant="rectangular"
        />
        <Skeleton
          className={clsx(classes.barSkeleton, classes.barSkeleton90)}
          variant="rectangular"
        />
        <Skeleton
          className={clsx(classes.barSkeleton, classes.barSkeleton40)}
          variant="rectangular"
        />
        <Skeleton
          className={clsx(classes.barSkeleton, classes.barSkeleton30)}
          variant="rectangular"
        />
        <Skeleton
          className={clsx(classes.barSkeleton, classes.barSkeleton10)}
          variant="rectangular"
        />
        <Skeleton
          className={clsx(classes.barSkeleton, classes.barSkeleton50)}
          variant="rectangular"
        />
        <Skeleton
          className={clsx(classes.barSkeleton, classes.barSkeleton60)}
          variant="rectangular"
        />
        <Skeleton
          className={clsx(classes.barSkeleton, classes.barSkeleton20)}
          variant="rectangular"
        />
      </div>
    ) : (
      <div
        className={clsx(classes.noDataContainer, classes.noDataBackground)}
        style={{
          flexDirection: 'column',
          textAlign: 'center',
        }}
      >
        <h1 style={{ paddingTop: '35px' }}>
          <b>Well Hey There...</b>
        </h1>
        <h2>Looks like you do not have any data yet!</h2>
        <img style={{ height: '500px' }} alt="logo" src={getLogo()} />
        <br />
      </div>
    );
  }
);

export const hhmmss = (seconds: number) => {
  const negativeDuration = seconds < 0;
  if (negativeDuration) {
    seconds *= -1;
  }
  let secs = seconds;
  let mins = Math.floor(secs / 60);
  const hours = Math.floor(mins / 60);
  secs = Math.floor(secs % 60);
  if (hours > 0) {
    mins = Math.floor(mins % 60);
    return (
      (negativeDuration ? '-' : '') + `${pad(hours)}:${pad(mins)}:${pad(secs)}`
    );
  }
  return (negativeDuration ? '-' : '') + `${pad(mins)}:${pad(secs)}`;
};
